/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, { useEffect, useRef, useState } from 'react';
import { find, findIndex, concat, maxBy, get as _get } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { buildGeoJsonForOverlayOverlay, buildGeoJsonForCompanyOverlay, buildGeoJsonForSupplierOverlay } from './GeoJsonBuilder';
import { useResizeDetector } from 'react-resize-detector';
import { getListImportOverlayRecord, getListImportCompanyRecord, getListImportContactRecord } from "../../common/services/ListService";
import { getSupplierRecordBySourceId } from "../../common/services/SupplierService";

import Spinner from 'react-bootstrap/Spinner';
import ExploreMapFlyout from './ExploreMapFlyout';

import Alert from "react-bootstrap/Alert";
import mapboxgl from 'mapbox-gl';

import { APP_COLORS } from '../../common/Constants';
import { TYPES as SourceTypes } from '../../admin/sources/SourceTypes';
import { setOverlaySelect } from "./exploreSlice";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";

// Import SVG as text for Vite compatibility
import svgIconsUrl from '../../common/assets/img/fa-icons/solid.svg?url';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

import './ExploreMap.scss';

// Helper function to load SVG and convert to data URL
const loadSvgIcon = async (iconName) => {
    try {
        const response = await fetch(svgIconsUrl);
        const svgText = await response.text();
        const parser = new DOMParser();
        const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
        const iconElement = svgDoc.getElementById(iconName);

        if (iconElement) {
            const newSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            newSvg.setAttribute('viewBox', iconElement.getAttribute('viewBox') || '0 0 512 512');
            newSvg.setAttribute('width', '32');
            newSvg.setAttribute('height', '32');
            newSvg.appendChild(iconElement.cloneNode(true));

            const svgData = new XMLSerializer().serializeToString(newSvg);
            return "data:image/svg+xml;base64," + btoa(svgData);
        }
        return null;
    } catch (error) {
        console.error('Error loading SVG icon:', error);
        return null;
    }
};

mapboxgl.accessToken = 'pk.eyJ1Ijoib3BzLWF0LWdldHRvb2xiZWx0LWRvdC1jb20iLCJhIjoiY2tqYWVlazdqN2FoajJ2cGRyaDM0b2w5dSJ9.0ghFxBToWVY2tiKhPDrs6w';

if (typeof mapboxgl.prewarm === 'function') {
    mapboxgl.prewarm();
}
mapboxgl.telemetryDisabled = true;

const initialCoordinates = [-98.2883, 41.1289];
const initialZoom = 3.75;

export default function ExploreMap(props) {
    const dispatch = useDispatch();

    const exploreDataState = useSelector(state => state.explore);
    const universeState = useSelector(state => state.universe);
    const universeQueryState = useSelector(state => state.universeQuery);

    const currentUniverse = universeState.currentUniverse || 'BUSINESS';
    const exploreByContact = universeState.universes[currentUniverse]?.exploreByContact ?? false;
    const modeKey = exploreByContact ? 'CONTACT' : 'COMPANY';

    const currentResults = universeQueryState.results[currentUniverse];
    const mapData = currentResults?.[modeKey]?.mapData;
    const mapStatus = currentResults?.mapStatus || 'idle';
    const mapErrorMessage = currentResults?.mapErrorMessage || null;
    const universeQueryLoading = universeQueryState.searchLoading || universeQueryState.countLoading;

    const [loading, setLoading] = useState(true);

    const [exploreMapMode, setExploreMapMode] = useState('CITY');
    const [registeredListeners, setRegisteredListeners] = useState([]);
    const [renderedOverlays, setRenderedOverlays] = useState([]);
    const [renderedProducts, setRenderedProducts] = useState([]);

    const [exploreMapFlyoutMode, setExploreMapFlyoutMode] = useState('CLUSTERED');
    const [exploreMapFlyoutDetailMode, setExploreMapFlyoutDetailMode] = useState('NONE');

    const [showFlyout, setShowFlyout] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);

    const [map, setMap] = useState(null);
    const mapContainer = useRef(null);

    const { ref: resizeRef } = useResizeDetector({
        onResize: () => {
            map?.resize();
        }
    });

    const handleDataPointMouseEnter = (e) => {
        e.target.getCanvas().style.cursor = 'pointer';
    }

    const handleDataPointMouseLeave = (e) => {
        e.target.getCanvas().style.cursor = '';
    }

    // Initial Map Load
    useEffect(() => {
        if (map || !mapContainer.current) return;

        const mapInstance = new mapboxgl.Map({
            container: mapContainer.current,
            style: "mapbox://styles/mapbox/light-v10",
            center: initialCoordinates,
            zoom: initialZoom,
            collectResourceTiming: false
        });

        mapInstance.addControl(new MapboxGeocoder({
            accessToken: mapboxgl.accessToken,
            mapboxgl: mapboxgl,
            placeholder: 'City, State, Zip ...',
            marker: false,
            countries: 'us',
        }), 'top-left');

        const nav = new mapboxgl.NavigationControl({ visualizePitch: true });
        mapInstance.addControl(nav, 'top-left');

        mapInstance.on('load', () => {
            if (mapInstance && mapData && mapData.features && mapData.features.length > 0) {
                buildDefaultProductMap(mapInstance, mapData, true);
            } else {
                setMap(mapInstance);
                setLoading(false);
            }
        });

        return () => {
            if (mapInstance) {
                mapInstance.remove();
            }
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (map && mapData) {
            if (!map.getSource('data')) {
                buildDefaultProductMap(map, mapData);
            } else {
                map.getSource('data').setData(mapData);
            }
        }
    }, [mapData, map]);

    useEffect(() => {
        if (map && exploreDataState.overlaySelect) {
            handleOverlaySelection('ADD', exploreDataState.overlaySelect);
        }
        // eslint-disable-next-line
    }, [map]);

    const buildDefaultProductMap = (map, data, disableLoading = false) => {
        if (map.getLayer('unclustered-data-point')) map.removeLayer('unclustered-data-point');
        if (map.getSource('unclustered-data')) map.removeSource('unclustered-data');

        setShowFlyout(false);

        const layers = ['clusters', 'cluster-count', 'unclustered-point', 'unclustered-count', 'unclustered-record-point'];
        layers.forEach((l) => {
            if (map.getLayer(l)) map.removeLayer(l);
        });

        if (map.getSource('data')) map.removeSource('data');
        map.addSource('data', {
            type: 'geojson',
            data: data,
            cluster: true,
            clusterMaxZoom: 5,
            clusterRadius: 50,
            clusterMinPoints: 2,
            clusterProperties: {
                sum_companies: ['+', ['get', 'count_companies']],
                sum_contacts: ['+', ['get', 'count_contacts']],
            }
        });

        map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'data',
            layout: { 'visibility': 'visible' },
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': APP_COLORS.orange,
                'circle-radius': [
                    'step',
                    ['get', 'sum_companies'],
                    30, 1000,
                    40, 10000,
                    50
                ]
            }
        });

        map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'data',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{sum_companies}',
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'visibility': 'visible'
            },
            paint: { 'text-color': '#fff' }
        });

        map.addLayer({
            id: 'cluster-location_place_point',
            type: 'circle',
            source: 'data',
            layout: { 'visibility': 'visible' },
            filter: ['all', ['>=', 'count_companies', 1], ['!has', 'point_count'], ['!has', 'company_score']],
            paint: {
                'circle-radius': 20,
                'circle-color': APP_COLORS.orange
            },
        });

        map.addLayer({
            id: 'cluster-location_place_count',
            type: 'symbol',
            source: 'data',
            filter: ['all', ['>=', 'count_companies', 1], ['!has', 'point_count'], ['!has', 'company_score']],
            layout: {
                'text-field': '{count_companies}',
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
                'visibility': 'visible'
            },
            paint: { 'text-color': '#fff' }
        });

        map.addLayer({
            id: 'unclustered-record-point',
            type: 'circle',
            source: 'data',
            layout: { 'visibility': 'visible' },
            filter: ['all', ['==', 'count_companies', 1], ['!has', 'point_count'], ['has', 'company_score']],
            paint: {
                'circle-radius': [
                    'interpolate', ['exponential', 2], ['zoom'],
                    6, 2, 10, 4, 15, 8, 20, 90
                ],
                'circle-color': APP_COLORS.orange,
                'circle-opacity': [
                    'case',
                    ['>=', ['get', 'company_score'], 0.90], 1.0,
                    ['>=', ['get', 'company_score'], 0.80], 0.85,
                    ['>=', ['get', 'company_score'], 0.70], 0.75,
                    ['>=', ['get', 'company_score'], 0.60], 0.65,
                    0.6
                ],
            },
        });

        map.on('click', 'clusters', handleClusteredPointClick);
        map.on('click', 'cluster-location_place_point', handleUnclusterLocationPlaceClusterPointClick);
        map.on('click', 'unclustered-record-point', handleUnclusteredRecordDataPointClick);

        map.on('mouseenter', 'clusters', handleDataPointMouseEnter);
        map.on('mouseleave', 'clusters', handleDataPointMouseLeave);
        map.on('mouseenter', 'cluster-location_place_point', handleDataPointMouseEnter);
        map.on('mouseleave', 'cluster-location_place_point', handleDataPointMouseLeave);
        map.on('mouseenter', 'unclustered-record-point', handleDataPointMouseEnter);
        map.on('mouseleave', 'unclustered-record-point', handleDataPointMouseLeave);

        setMap(map);
        if (disableLoading) setLoading(false);
    };

    const handleClusteredPointClick = (e) => {
        const mapTarget = e.target;
        let clusterFeatures = mapTarget.queryRenderedFeatures(e.point, { layers: ['clusters'] });
        setShowFlyout(false);
        if (clusterFeatures && clusterFeatures.length > 0) {
            let clusterId = clusterFeatures[0].properties.cluster_id;
            var pointCount = clusterFeatures[0].properties.point_count;
            var clusterSource = mapTarget.getSource('data');

            clusterSource.getClusterLeaves(clusterId, pointCount, 0, function (error, features) {
                if (features && features.length > 0) {
                    const bestMatchLocation = maxBy(features, (feature) => feature.properties.count_companies);
                    setExploreMapFlyoutDetailMode('NONE');
                    setSelectedPoint({
                        type: 'PRODUCT',
                        geometry: clusterFeatures[0].geometry,
                        countCompanies: clusterFeatures[0].properties.sum_companies || 0,
                        countContacts: clusterFeatures[0].properties.sum_contacts || 0,
                        selectedLocations: features.map((feature) => feature.properties),
                        bestLocation: bestMatchLocation.properties
                    });
                    setExploreMapFlyoutDetailMode('PRODUCT_DATA_CLUSTERED');
                    setShowFlyout(true);
                }
            });
        }
    };

    const handleUnclusterLocationPlaceClusterPointClick = (e) => {
        setExploreMapFlyoutDetailMode('NONE');
        setSelectedPoint({
            type: 'PRODUCT',
            geometry: e.features[0].geometry,
            countCompanies: e.features[0].properties.count_companies,
            countContacts: e.features[0].properties.count_contacts,
            selectedLocations: [e.features[0].properties],
            bestLocation: e.features[0].properties
        });
        setExploreMapFlyoutDetailMode('PRODUCT_DATA_CLUSTERED');
        setShowFlyout(true);
    };

    const handleUnclusteredRecordDataPointClick = (e) => {
        setShowFlyout(false);
        setExploreMapFlyoutDetailMode('NONE');
        setSelectedPoint({
            type: 'PRODUCT',
            geometry: e.features[0].geometry,
            properties: e.features[0].properties
        });
        setExploreMapFlyoutMode('PRODUCT_DATA_RECORD_POINT');
        setExploreMapFlyoutDetailMode('PRODUCT_DATA_RECORD_POINT');
        setShowFlyout(true);
    };

    const handleOverlaySelection = (action, selectedOption) => {
        const sourceId = `loc-${selectedOption.value}-data`;
        const layerId = `loc-${selectedOption.value}-point`;
        const imgId = `loc-${selectedOption.value}-img`;

        if (action === 'REMOVE') {
            if (map.getLayer(layerId)) {
                map.off('mouseenter', layerId, handleDataPointMouseEnter);
                map.off('mouseleave', layerId, handleDataPointMouseLeave);
                map.off('click', layerId, handleLocationPointClick);
                map.off('click', layerId, handleOverlayImportCompanyPointClick);
                map.off('click', layerId, handleOverlayListContactPointClick);
                map.removeLayer(layerId);
            }
            if (map.getSource(sourceId)) map.removeSource(sourceId);
            if (map.hasImage(imgId)) map.removeImage(imgId);

            const _renderedOverlays = [...renderedOverlays];
            const _selectedOptionIdx = findIndex(_renderedOverlays, { value: selectedOption.value });
            if(_selectedOptionIdx > -1) _renderedOverlays.splice(_selectedOptionIdx, 1);

            setRenderedOverlays(_renderedOverlays);
            setExploreMapFlyoutDetailMode('NONE');
            dispatch(setOverlaySelect(null));
        }

        if (action === 'ADD') {
            setLoading(true);
            _getOverlayRecords(selectedOption.value, selectedOption.type)
                .then((response) => {
                    if (selectedOption.type === 'IMPORT_OVERLAY') {
                        const geoJson = buildGeoJsonForOverlayOverlay(response, 'ListImportOverlayRecord');
                        const iconUrl = _get(selectedOption, 'config.map.overlay.imgSecureUrl', null);
                        if (iconUrl) {
                            map.loadImage(iconUrl, (error, image) => {
                                if (error) throw error;
                                map.addImage(imgId, image);
                                map.addSource(sourceId, { 'type': 'geojson', 'data': geoJson });
                                map.addLayer({
                                    'id': layerId,
                                    'type': 'symbol',
                                    'source': sourceId,
                                    'layout': {
                                        'icon-image': imgId,
                                        'icon-size': 1,
                                        'visibility': 'visible'
                                    }
                                });
                            });
                        } else {
                            const _img = new Image(24, 24);
                            const iconSrc = _get(selectedOption, 'config.map.overlay.img', 'map-marker-alt');
                            const iconColor = _get(selectedOption, 'config.map.overlay.color', APP_COLORS.orange);
                            const tcuMatchedColor = _get(selectedOption, 'config.map.overlay.tcuMatchedcolor', '#0171fe');

                            _img.onload = () => {
                                map.addImage(imgId, _img, { "sdf": "true" });
                                map.addSource(sourceId, { 'type': 'geojson', 'data': geoJson });
                                map.addLayer({
                                    'id': layerId,
                                    'type': 'symbol',
                                    'source': sourceId,
                                    'layout': {
                                        'icon-image': imgId,
                                        'icon-size': 1,
                                        'visibility': 'visible'
                                    },
                                    'paint': {
                                        'icon-halo-color': '#fff',
                                        'icon-halo-width': 2,
                                        'icon-color': [
                                            'case',
                                            ['to-boolean', ['get', 'locationId']],
                                            tcuMatchedColor,
                                            iconColor
                                        ]
                                    }
                                });
                            };

                            loadSvgIcon(iconSrc).then(dataUrl => {
                                if (dataUrl) _img.src = dataUrl;
                            }).catch(error => {
                                console.error('Error loading icon:', iconSrc, error);
                            });
                        }

                        if (!find(registeredListeners, { value: selectedOption.value })) {
                            map.on('mouseenter', layerId, handleDataPointMouseEnter);
                            map.on('mouseleave', layerId, handleDataPointMouseLeave);
                            map.on('click', layerId, handleLocationPointClick);
                            setRegisteredListeners(concat(registeredListeners, [selectedOption]));
                        }
                        setLoading(false);
                        setRenderedOverlays(concat(renderedOverlays, [selectedOption]));
                        dispatch(setOverlaySelect(selectedOption));
                    }

                    if (selectedOption.type === 'IMPORT_COMPANY') {
                        const geoJson = buildGeoJsonForCompanyOverlay(response, 'ListImportCompanyRecord');
                        const tcuMatchedColor = selectedOption?.config?.map?.overlay?.tcuMatchedColor || '#0171fe';

                        map.addSource(sourceId, { type: 'geojson', data: geoJson });
                        map.addLayer({
                            id: layerId,
                            type: 'circle',
                            source: sourceId,
                            'paint': {
                                'circle-radius': [
                                    'interpolate', ['exponential', 2], ['zoom'],
                                    6, 2, 10, 4, 15, 8, 20, 90
                                ],
                                'circle-color': [
                                    'case',
                                    ['to-boolean', ['get', 'companyId']],
                                    tcuMatchedColor,
                                    selectedOption.config.map.overlay.color
                                ],
                            },
                        }, map.getLayer('clusters') ? 'clusters' : undefined);

                        if (!find(renderedOverlays, { value: selectedOption.value })) {
                            map.on('mouseenter', layerId, handleDataPointMouseEnter);
                            map.on('mouseleave', layerId, handleDataPointMouseLeave);
                            map.on('click', layerId, handleOverlayImportCompanyPointClick);
                            setRenderedOverlays(concat(renderedOverlays, [selectedOption]));
                        }
                        setLoading(false);
                    }

                    if (selectedOption.type === 'IMPORT_CONTACT') {
                        const geoJson = buildGeoJsonForCompanyOverlay(response, 'ListImportContactRecord');
                        const color = _get(selectedOption, 'config.map.overlay.color', '#f8f9fa');
                        const tcuMatchedColor = _get(selectedOption, 'config.map.overlay.tcuMatchedcolor', '#0171fe');

                        map.addSource(sourceId, { type: 'geojson', data: geoJson });
                        map.addLayer({
                            id: layerId,
                            type: 'circle',
                            source: sourceId,
                            'paint': {
                                'circle-radius': [
                                    'interpolate', ['exponential', 2], ['zoom'],
                                    6, 2, 10, 4, 15, 8, 20, 90
                                ],
                                'circle-color': [
                                    'case',
                                    ['to-boolean', ['get', 'contactId']],
                                    tcuMatchedColor,
                                    color
                                ],
                            },
                        }, map.getLayer('clusters') ? 'clusters' : undefined);

                        if (!find(renderedOverlays, { value: selectedOption.value })) {
                            map.on('mouseenter', layerId, handleDataPointMouseEnter);
                            map.on('mouseleave', layerId, handleDataPointMouseLeave);
                            map.on('click', layerId, handleOverlayListContactPointClick);
                            setRenderedOverlays(concat(renderedOverlays, [selectedOption]));
                        }
                        setLoading(false);
                    }

                    if (selectedOption.type === SourceTypes.SUPPLIER.id) {
                        const geoJson = buildGeoJsonForSupplierOverlay(response);
                        const _img = new Image(24, 24);
                        const iconSrc = _get(selectedOption, 'config.map.overlay.img', 'map-marker-alt');
                        const iconColor = _get(selectedOption, 'config.map.overlay.color', APP_COLORS.orange);
                        const tcuMatchedColor = _get(selectedOption, 'config.map.overlay.tcuMatchedcolor', '#0171fe');

                        _img.onload = () => {
                            map.addImage(imgId, _img, { "sdf": "true" });
                            map.addSource(sourceId, { 'type': 'geojson', 'data': geoJson });
                            map.addLayer({
                                'id': layerId,
                                'type': 'symbol',
                                'source': sourceId,
                                'layout': {
                                    'icon-image': imgId,
                                    'icon-size': 1,
                                    'visibility': 'visible'
                                },
                                'paint': {
                                    'icon-halo-color': '#fff',
                                    'icon-halo-width': 2,
                                    'icon-color': [
                                        'case',
                                        ['to-boolean', ['get', 'locationId']],
                                        tcuMatchedColor,
                                        iconColor
                                    ]
                                }
                            });
                        };

                        loadSvgIcon(iconSrc).then(dataUrl => {
                            if (dataUrl) _img.src = dataUrl;
                        }).catch(error => {
                            console.error('Error loading icon:', iconSrc, error);
                        });

                        if (!find(renderedOverlays, { value: selectedOption.value })) {
                            map.on('mouseenter', layerId, handleDataPointMouseEnter);
                            map.on('mouseleave', layerId, handleDataPointMouseLeave);
                            map.on('click', layerId, handleOverlaySupplierPointClick);
                            setRenderedOverlays(concat(renderedOverlays, [selectedOption]));
                        }
                        setLoading(false);
                    }
                })
        }
    }

    const handleLocationPointClick = (e) => {
        setShowFlyout(true);
        setExploreMapFlyoutDetailMode('NONE');
        setSelectedPoint({
            type: 'OVERLAY',
            geometry: e.features[0].geometry,
            properties: e.features[0].properties,
        });
        setExploreMapFlyoutDetailMode('OVERLAY');
    };

    const handleOverlayImportCompanyPointClick = (e) => {
        if (e.features[0] && e.features[0].properties && e.features[0].properties.companyId) {
            setExploreMapFlyoutDetailMode('NONE');
            setSelectedPoint({
                type: 'PRODUCT',
                geometry: e.features[0].geometry,
                properties: {
                    companyId: e.features[0].properties.companyId
                }
            });
            setExploreMapFlyoutMode('PRODUCT_DATA_RECORD_POINT');
            setExploreMapFlyoutDetailMode('PRODUCT_DATA_RECORD_POINT');
            setShowFlyout(true);
        } else {
            setExploreMapFlyoutDetailMode('NONE');
            setSelectedPoint({
                type: 'OVERLAY',
                geometry: e.features[0].geometry,
                properties: {
                    ...e.features[0].properties,
                    listType: 'IMPORT_COMPANY'
                },
            });
            setExploreMapMode('ADDRESS');
            setExploreMapFlyoutDetailMode('UNCLUSTERED');
            setShowFlyout(true);
        }
    };

    const handleOverlayListContactPointClick = (e) => {
        setExploreMapFlyoutDetailMode('NONE');
        setSelectedPoint({
            type: 'OVERLAY',
            geometry: e.features[0].geometry,
            properties: {
                ...e.features[0].properties,
                listType: 'IMPORT_CONTACT'
            },
        });
        setExploreMapMode('ADDRESS');
        setExploreMapFlyoutDetailMode('UNCLUSTERED');
        setShowFlyout(true);
    };

    const handleOverlaySupplierPointClick = (e) => {
        setExploreMapFlyoutDetailMode('NONE');
        setSelectedPoint({
            type: 'OVERLAY',
            geometry: e.features[0].geometry,
            properties: {
                ...e.features[0].properties,
                listType: 'SUPPLIER'
            },
        });
        setExploreMapMode('ADDRESS');
        setExploreMapFlyoutDetailMode('UNCLUSTERED');
        setShowFlyout(true);
    };

    const handleShowHideProductMap = () => {
        const layers = ['clusters', 'cluster-count', 'unclustered-point', 'unclustered-count', 'cluster-location_place_point', 'cluster-location_place_count',
            'unclustered-data-point', 'unclustered-record-point'];
        layers.forEach((l) => {
            const visibility = map.getLayoutProperty(l, 'visibility');
            if (visibility === 'visible') {
                map.setLayoutProperty(l, 'visibility', 'none');
                setRenderedProducts([]);
            }
            if (visibility === 'none') {
                map.setLayoutProperty(l, 'visibility', 'visible');
                setRenderedProducts(['TCU']);
            }
        });
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {(
                loading ||
                universeQueryLoading ||
                mapStatus === 'loading'
            ) && (
                <div className="explore-map-spinner-overlay">
                    <Spinner
                        as="span"
                        animation="border"
                        size="lg"
                        role="status"
                        aria-hidden="true"
                        variant="info"
                    />
                </div>
            )}

            {(mapStatus === 'error') && (
                <div>
                    <Alert
                        variant={"danger"}
                        dismissible={false}
                        show={true}
                        transition={true}
                    >
                        {mapErrorMessage || 'An error occurred while loading map data.'}
                    </Alert>
                </div>
            )}

            {!showFlyout && mapStatus !== 'error' && (
                <div className='explore-map-flyout-hide' onClick={() => setShowFlyout(!showFlyout)}>
                    <FontAwesomeIcon
                        color={APP_COLORS['tech-blue']}
                        icon={faLayerGroup}
                    />
                </div>
            )}

            {showFlyout && (
                <ExploreMapFlyout
                    setShowFlyout={setShowFlyout}
                    currentFilters={props.currentFilters}
                    exploreMode={props.exploreMode}
                    exploreMapMode={exploreMapMode}
                    exploreMapFlyoutDetailMode={exploreMapFlyoutDetailMode}
                    renderedOverlays={renderedOverlays}
                    handleOverlaySelection={handleOverlaySelection}
                    renderedProducts={renderedProducts}
                    handleShowHideProductMap={handleShowHideProductMap}
                    selectedPoint={selectedPoint}
                />
            )}

            <div
                ref={(el) => {
                    mapContainer.current = el;
                    if (resizeRef) resizeRef.current = el;
                }}
                className="mapContainer"
                id="mapContainerID"
            />
        </div>
    );
}

async function _getOverlayRecords(listId, type) {
    if (type === 'IMPORT_OVERLAY') {
        return getListImportOverlayRecord(listId);
    }
    if (type === 'IMPORT_COMPANY') {
        return getListImportCompanyRecord(listId);
    }
    if (type === 'IMPORT_CONTACT') {
        return getListImportContactRecord(listId);
    }
    if (type === SourceTypes.SUPPLIER.id) {
        return getSupplierRecordBySourceId(listId);
    }
}